import {
  CalendarOutlined,
  CarryOutOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LoadingOutlined,
  SettingOutlined,
  SignatureOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, Col, Popover, Row, Spin } from 'antd';
import type { PopoverProps } from 'antd';
import type { ReactNode } from 'react';
import type { EmployeeActions } from 'types/employeeActions';
import User from 'types/user';

interface EmployeeActionButtonProps {
  additionalClassName?: string;
  dataCy: string;
  disabled?: boolean;
  icon: ReactNode;
  loading?: boolean;
  onClick: () => void | Promise<void>;
  text: string;
}

const EmployeeActionButton = ({
  additionalClassName,
  dataCy,
  disabled = false,
  icon,
  loading = false,
  onClick,
  text,
}: EmployeeActionButtonProps) => (
  <Spin spinning={loading} indicator={<LoadingOutlined />}>
    <Button
      data-cy={dataCy}
      type="text"
      className={`setting-btn ${additionalClassName ?? ''}`}
      onClick={() => void onClick()}
      disabled={disabled}
    >
      <span>{icon}</span>
      <p>{text}</p>
    </Button>
  </Spin>
);

interface EmployeeActionsPopoverProps {
  actions: EmployeeActions;
  buttonText?: string;
  dataCy?: string;
  employee: User;
  impersonatingEmployeeId: string | null;
  placement?: PopoverProps['placement'];
}

export const EmployeeActionsPopover = ({
  actions,
  buttonText,
  dataCy,
  employee,
  impersonatingEmployeeId,
  placement = 'top',
}: EmployeeActionsPopoverProps) => {
  const content = (
    <Row justify="center" className="settings">
      <Col>
        <EmployeeActionButton
          dataCy="employee-detail"
          onClick={() => actions.onOpenDetail(employee)}
          icon={<EyeOutlined />}
          text="Detalle"
        />
        <EmployeeActionButton
          dataCy="edit-employee-btn"
          onClick={() => actions.onEdit(employee)}
          icon={<EditOutlined />}
          text="Editar"
        />
        <EmployeeActionButton
          dataCy="employee-contracts"
          onClick={() => actions.onOpenContracts(employee)}
          icon={<SignatureOutlined />}
          text="Contratos"
        />
        <EmployeeActionButton
          dataCy="employee-kardex"
          onClick={() => actions.onOpenKardex(employee)}
          icon={<CalendarOutlined />}
          text="Kardex"
        />
        <EmployeeActionButton
          dataCy="impersonate-employee-btn"
          onClick={() => actions.onImpersonate(employee)}
          icon={<TeamOutlined />}
          text="Impersonar"
          loading={impersonatingEmployeeId === employee._id}
          disabled={!employee.status}
        />
        <EmployeeActionButton
          dataCy="reconcile-employee-incidences-btn"
          onClick={() => actions.onReconcileIncidences(employee)}
          icon={<CarryOutOutlined />}
          text="Conciliar incidencias"
          disabled={!employee.status}
        />
        <EmployeeActionButton
          dataCy="delete-employee-btn"
          onClick={() => actions.onDelete(employee)}
          icon={<DeleteOutlined />}
          text="Eliminar"
          additionalClassName="delete-button"
        />
      </Col>
    </Row>
  );

  return (
    <div
      className="settings"
      data-cy={dataCy ?? `user-${employee.idEmployee}-settings`}
    >
      <Popover
        placement={placement}
        content={content}
        trigger="click"
        className="settings-container"
      >
        <Button type="text" aria-label={buttonText ?? 'Opciones del empleado'}>
          <SettingOutlined />
          {buttonText}
        </Button>
      </Popover>
    </div>
  );
};
